import React, { useMemo } from "react";
import * as THREE from "three";
import { useNight } from "../lib/worldTheme";
import { scatterPositions, LAKES, mulberry32 } from "./helpers";

// A modern, detailed lake: layered water (shallow rim + deep core), a sandy
// shoreline, scattered lily pads, a ring of reeds and a couple of shore rocks.
function Lake({ x, z, r, night }) {
  const water = night ? "#15323D" : "#4F93A8";
  const waterDeep = night ? "#0E2530" : "#3C7C90";
  const sand = night ? "#241C18" : "#CDBC92";
  const pad = night ? "#28432F" : "#4F8A43";
  const reed = night ? "#2E4A33" : "#5C8A3E";
  const reedTip = night ? "#3C5A40" : "#C9B45A";
  const rock = night ? "#3A3F49" : "#9A968C";

  const detail = useMemo(() => {
    const rng = mulberry32(Math.round((x + 100) * 17 + (z + 100) * 7 + r * 13));
    // lily pads floating on the water
    const pads = [];
    const padCount = 7;
    for (let i = 0; i < padCount; i++) {
      const a = rng() * Math.PI * 2;
      const rad = rng() * r * 0.74;
      pads.push({
        x: Math.cos(a) * rad,
        z: Math.sin(a) * rad,
        s: 0.34 + rng() * 0.32,
        rot: rng() * Math.PI,
        flower: rng() > 0.55,
        fc: rng() > 0.5 ? "#FF7FB0" : "#FFFFFF",
      });
    }
    // reeds clustered around the rim
    const reeds = [];
    const reedCount = 26;
    for (let i = 0; i < reedCount; i++) {
      const a = (i / reedCount) * Math.PI * 2 + (rng() - 0.5) * 0.22;
      const rad = r + (rng() - 0.5) * 0.7;
      reeds.push({ x: Math.cos(a) * rad, z: Math.sin(a) * rad, h: 0.85 + rng() * 0.9, lean: (rng() - 0.5) * 0.25, a });
    }
    // shore rocks
    const rocks = [];
    const rockCount = 5;
    for (let i = 0; i < rockCount; i++) {
      const a = rng() * Math.PI * 2;
      const rad = r + 0.5 + rng() * 0.7;
      rocks.push({ x: Math.cos(a) * rad, z: Math.sin(a) * rad, s: 0.4 + rng() * 0.55, rot: rng() * Math.PI });
    }
    return { pads, reeds, rocks };
  }, [x, z, r]);

  return (
    <group position={[x, 0, z]}>
      {/* sandy shoreline ring */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]} receiveShadow>
        <ringGeometry args={[r - 0.1, r + 1.1, 40]} />
        <meshStandardMaterial color={sand} roughness={1} side={THREE.DoubleSide} />
      </mesh>
      {/* shallow water surface */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.07, 0]}>
        <circleGeometry args={[r, 40]} />
        <meshStandardMaterial color={water} roughness={0.2} metalness={0.3} emissive={night ? "#0c2730" : "#000"} emissiveIntensity={night ? 0.34 : 0} />
      </mesh>
      {/* deep-water core for depth */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.085, 0]}>
        <circleGeometry args={[r * 0.66, 36]} />
        <meshStandardMaterial color={waterDeep} roughness={0.16} metalness={0.4} transparent opacity={0.85} />
      </mesh>

      {/* lily pads */}
      {detail.pads.map((p, i) => (
        <group key={`pad${i}`} position={[p.x, 0.1, p.z]}>
          <mesh rotation={[-Math.PI / 2, 0, p.rot]}>
            <circleGeometry args={[p.s, 7, 0.5, Math.PI * 1.82]} />
            <meshStandardMaterial color={pad} roughness={0.8} side={THREE.DoubleSide} />
          </mesh>
          {p.flower && (
            <mesh position={[0, 0.06, 0]}>
              <icosahedronGeometry args={[0.1, 0]} />
              <meshStandardMaterial color={p.fc} emissive={p.fc} emissiveIntensity={night ? 0.4 : 0.12} roughness={0.7} flatShading />
            </mesh>
          )}
        </group>
      ))}

      {/* reeds around the rim */}
      {detail.reeds.map((rd, i) => (
        <group key={`reed${i}`} position={[rd.x, 0, rd.z]} rotation={[rd.lean, rd.a, 0]}>
          <mesh position={[0, rd.h / 2, 0]} castShadow>
            <cylinderGeometry args={[0.03, 0.05, rd.h, 5]} />
            <meshStandardMaterial color={reed} roughness={1} />
          </mesh>
          <mesh position={[0, rd.h + 0.12, 0]} castShadow>
            <coneGeometry args={[0.07, 0.3, 6]} />
            <meshStandardMaterial color={reedTip} roughness={0.9} />
          </mesh>
        </group>
      ))}

      {/* shore rocks */}
      {detail.rocks.map((rk, i) => (
        <mesh key={`rock${i}`} position={[rk.x, 0.14 * rk.s, rk.z]} rotation={[0.3, rk.rot, 0.2]} scale={rk.s} castShadow receiveShadow>
          <dodecahedronGeometry args={[0.5, 0]} />
          <meshStandardMaterial color={rock} roughness={1} flatShading />
        </mesh>
      ))}
    </group>
  );
}

// A lush, organic grass campus with a detailed corner lake.
export default function Ground() {
  const night = useNight();
  const grassBase = night ? "#1B3325" : "#6E9C52";
  const patchLight = night ? "#234231" : "#80B062";
  const patchDark = night ? "#15281D" : "#5C8A44";

  const patches = useMemo(
    () => scatterPositions({ count: 26, radius: 33, seed: 71, minSpacing: 3.4, obstacles: [], avoidDist: 0 }),
    []
  );

  return (
    <group>
      {/* grass island slab (flat top sits exactly at y=0) — tighter footprint */}
      <mesh position={[0, -0.6, 0]} receiveShadow>
        <boxGeometry args={[76, 1.2, 76]} />
        <meshStandardMaterial color={grassBase} roughness={1} />
      </mesh>

      {/* subtle grass tone clumps scattered across the field */}
      {patches.map((p, i) => (
        <mesh
          key={i}
          position={[p.x, 0.015 + (i % 4) * 0.004, p.z]}
          rotation={[-Math.PI / 2, 0, p.a]}
          receiveShadow
        >
          <circleGeometry args={[2.0 + p.rnd * 3.6, 10]} />
          <meshStandardMaterial color={i % 2 ? patchLight : patchDark} roughness={1} />
        </mesh>
      ))}

      {/* detailed corner lake(s) */}
      {LAKES.map((l, i) => (
        <Lake key={i} x={l.x} z={l.z} r={l.r} night={night} />
      ))}
    </group>
  );
}
